import { useFetchHtml } from '@common/hooks/useFetchHtml';
import { useEffect, useState } from 'react';
import { useFetchSubmissionTaskData } from '@common/hooks/useFetchSubmissionTaskData';
import { useFetchDocumentMetadata } from '@common/hooks/useFetchDocumentMetadata';
import { useParams } from 'react-router-dom';
import { DocumentType } from '@common/types';
import { SubmissionTaskStatuses } from '@common/services/types';

const PRINT_STYLESHEET_ID = 'print-page-stylesheet';

export const PrintPage = ({
  isAdmin = false
}: {
  isAdmin?: boolean;
}) => {
  const { taskId } = useParams<{ taskId?: string }>();
  const { htmlData } = useFetchHtml();
  const [ htmlDataExpanded, setHtmlDataExpanded] = useState('');
  const { submissionTask } = useFetchSubmissionTaskData(taskId ?? '');

  const documentTypeForHtml = submissionTask?.documentType === DocumentType.Pil ? DocumentType.Epil : submissionTask?.documentType;
  
  const { documentMetadata } = useFetchDocumentMetadata(
    submissionTask?.documentId as unknown as number,
    submissionTask?.version as unknown as number,
    documentTypeForHtml as DocumentType,
  );

  useEffect(() => {
    const isHtmlDataReadyToShow = submissionTask &&
      (
        isAdmin
        || submissionTask.documentType !== DocumentType.Smpc
        || submissionTask.status === SubmissionTaskStatuses.WithClientApproval
        || documentMetadata?.hasTvtFiles
      ) && !!htmlData;

    if (isHtmlDataReadyToShow) {
      const expandedHtml = htmlData.replaceAll("<details", "<details open=\"\"");
      setHtmlDataExpanded(expandedHtml);
    }

    let printStylesheet = document.getElementById(PRINT_STYLESHEET_ID) as HTMLLinkElement | null;

    if (!printStylesheet) {
      printStylesheet = document.createElement('link');
      printStylesheet.id = PRINT_STYLESHEET_ID;
      printStylesheet.rel = 'stylesheet';
      printStylesheet.href = '/print.css';
      printStylesheet.type = 'text/css';
      document.head.appendChild(printStylesheet);
    }

    if (isHtmlDataReadyToShow) {
      setTimeout(() => {
        window.print();
      }, 500);
    }

    return () => {
      const link = document.getElementById(PRINT_STYLESHEET_ID);
      if (link?.parentElement) {
        link.parentElement.removeChild(link);
      }
    };
  }, [htmlData, submissionTask, isAdmin, documentMetadata?.hasTvtFiles]);

  useEffect(() => {
    if (submissionTask) {
      const documentType = submissionTask.documentType || '';
      const id = submissionTask.documentId || '';
      const version = submissionTask.version || '';
      document.title = `${documentType} ${id} ${version}`;
      return;
    }

    if (taskId) {
      document.title = `task.${taskId}`;
    }
  }, [submissionTask, taskId]);

  return <div id="html-content" dangerouslySetInnerHTML={{ __html: htmlDataExpanded }} />
};
